import { useState } from "react";
import { useGetBulkOrdersQuery } from "@/services/api/orders/ordersApi";

/**
 * Custom hook for managing bulk order data
 * Provides pagination, search, and filter functionality for bulk orders
 */
export const useBulkOrderData = () => {
  // ==========================================
  // Pagination State
  // ==========================================
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  });

  // ==========================================
  // Search State
  // ==========================================
  const [search, setSearch] = useState("");

  // ==========================================
  // Filter State
  // ==========================================
  const [filters, setFilters] = useState({
    location_id: undefined,
    created_from: undefined,
    created_to: undefined,
  });

  // ==========================================
  // Pagination Handlers
  // ==========================================
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (per_page) => {
    setPagination({ page: 1, per_page });
  };

  // ==========================================
  // Search Handler
  // ==========================================
  const handleSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setSearch(trimmedSearch);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // ==========================================
  // Filter Handlers
  // ==========================================
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      location_id: undefined,
      created_from: undefined,
      created_to: undefined,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== undefined && value !== null && value !== ""
  );

  // ==========================================
  // API Query
  // ==========================================
  const queryParams = {
    page: pagination.page,
    per_page: pagination.per_page,
    search: search,
    ...(filters.location_id && { location_id: filters.location_id }),
    ...(filters.created_from && { created_from: filters.created_from }),
    ...(filters.created_to && { created_to: filters.created_to }),
  };

  const {
    data: apiResponse = {},
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetBulkOrdersQuery(queryParams);

  // Extract data from response
  const bulkOrdersData = apiResponse?.data?.data || [];
  const summary = apiResponse?.summary || {};
  const paginationInfo = {
    current_page: apiResponse?.data?.current_page || 1,
    last_page: apiResponse?.data?.last_page || 1,
    per_page: apiResponse?.data?.per_page || 10,
    total: apiResponse?.data?.total || 0,
  };

  return {
    // Data
    bulkOrdersData,
    summary,

    // Pagination info
    paginationInfo,

    // Pagination handlers
    handlePageChange,
    handlePageSizeChange,

    // Search state and handler
    search,
    handleSearch,

    // Filter state and handlers
    filters,
    handleFilterChange,
    handleClearFilters,
    hasActiveFilters,

    // Loading states
    isLoading,
    isFetching,

    // Error states
    error,
    isError: !!error,

    // Actions
    refetch,
  };
};
